import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LabelComponent } from '../../form/label/label.component';
import { CheckboxComponent } from '../../form/input/checkbox.component';
import { ButtonComponent } from '../../ui/button/button.component';
import { InputFieldComponent } from '../../form/input/input-field.component';
import { AuthService } from '../../../../pages/auth-pages/services/auth.service';
import { BusinessSelectionOption, LoginResponse } from '../../../../pages/auth-pages/models/user.model';
import { AlertComponent } from '../../ui/alert/alert.component';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-signin-form',
  imports: [
    CommonModule,
    LabelComponent,
    CheckboxComponent,
    ButtonComponent,
    InputFieldComponent,
    RouterModule,
    FormsModule,
    AlertComponent
  ],
  templateUrl: './signin-form.component.html',
  styles: ``
})
export class SigninFormComponent implements OnInit {

  showPassword = false;
  isChecked = false;

  email = '';
  password = '';
  isLoading = false;
  isGoogleLoading = false;
  isMicrosoftLoading = false;
  errorMessage = '';
  /** INC 17.5: explica por qué se cerró la sesión anterior (?reason=...). */
  sessionNotice = '';

  // Selección de empresa (CASO B): el usuario pertenece a varias empresas.
  showBusinessSelection = false;
  businessOptions: BusinessSelectionOption[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const oauthError = this.route.snapshot.queryParamMap.get('oauthError');
    if (oauthError) {
      this.errorMessage = this.mapOAuthError(oauthError);
    }
    this.sessionNotice = this.mapLogoutReason(this.route.snapshot.queryParamMap.get('reason'));
  }

  /** Traduce el `?reason=` con el que AuthService/interceptor redirigen a /signin. */
  private mapLogoutReason(reason: string | null): string {
    switch (reason) {
      case 'inactividad':
        return 'Tu sesión se cerró por inactividad. Vuelve a iniciar sesión para continuar.';
      case 'sesion-expirada':
        return 'Tu sesión expiró. Vuelve a iniciar sesión para continuar.';
      case 'sesion-cerrada':
        return 'Se cerró la sesión desde otra pestaña.';
      default:
        return '';
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onSignIn() {
    this.isLoading = true;
    this.errorMessage = '';

    //Validar campos vacios
    if (!this.email.trim() || !this.password.trim()) {
      this.errorMessage = 'Por favor, completa los campos';
      this.isLoading = false;
      return;
    }

    this.authService.login(this.email, this.password).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.requiresBusinessSelection) {
          // CASO B: mostrar el selector de empresa; aún no hay sesión.
          this.businessOptions = response.businesses ?? [];
          this.showBusinessSelection = true;
          return;
        }
        this.completeLogin(response);
      },
      error: (error) => this.handleLoginError(error),
    });
  }

  /** El usuario eligió una empresa: reintenta el login con el businessId. */
  selectBusiness(option: BusinessSelectionOption): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authService.login(this.email, this.password, option.businessId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.requiresBusinessSelection) {
          // No debería ocurrir al enviar businessId; mantener el selector.
          this.businessOptions = response.businesses ?? this.businessOptions;
          return;
        }
        this.toast.success({ title: 'Empresa seleccionada', message: `Ingresaste a ${option.businessName}.` });
        this.completeLogin(response);
      },
      error: (error) => this.handleLoginError(error),
    });
  }

  /** Vuelve al formulario de credenciales desde el selector de empresa. */
  backToCredentials(): void {
    this.showBusinessSelection = false;
    this.businessOptions = [];
    this.errorMessage = '';
  }

  private completeLogin(_response: LoginResponse): void {
    this.showBusinessSelection = false;
    this.router.navigate(['/contactos']);
  }

  private handleLoginError(error: { status?: number; error?: { message?: string } }): void {
    this.isLoading = false;
    console.error('Login error:', error);

    const status = error?.status ?? 0;
    const backendMsg = String(error?.error?.message ?? '').trim();
    const lower = backendMsg.toLowerCase();

    // Errores de red / servidor: no hay mensaje útil del backend.
    if (status === 0) {
      this.errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
      return;
    }
    if (status === 429) {
      this.errorMessage = 'Demasiados intentos. Espera un momento e intenta nuevamente.';
      return;
    }
    if (status >= 500) {
      this.errorMessage = 'Hubo un problema en el servidor. Intenta nuevamente en unos momentos.';
      return;
    }

    // 400 / 401 / 409: traducimos los mensajes conocidos del backend (en inglés) al español,
    // pero para cualquier otro mensaje específico mostramos el `message` del backend tal cual.
    // INC 13: empresa inactiva (backend manda el string en español) → mensaje claro y distinto
    // del genérico de credenciales y del de usuario inactivo.
    if (lower.includes('empresa está inactiva') || lower.includes('empresa inactiva')) {
      this.errorMessage = 'Tu empresa está inactiva. Contacta al administrador.';
    } else if (lower.includes('inactive')) {
      this.errorMessage = 'Tu cuenta está inactiva. Contacta al administrador para reactivarla.';
    } else if (lower.includes('invalid email or password') || lower.includes('bad credentials')) {
      this.errorMessage = 'Correo o contraseña incorrectos.';
    } else if (backendMsg) {
      this.errorMessage = backendMsg;
    } else {
      this.errorMessage = 'No se pudo iniciar sesión. Intenta nuevamente.';
    }
  }

  onGoogleSignIn() {
    this.isGoogleLoading = true;
    this.errorMessage = '';

    this.authService.getGoogleAuthorizeUrl().subscribe({
      next: (response) => {
        this.isGoogleLoading = false;
        window.location.href = response.url;
      },
      error: (error) => {
        this.isGoogleLoading = false;
        this.errorMessage = error?.error?.message || 'No se pudo iniciar sesión con Google. Por favor intenta nuevamente';
      }
    });
  }

  onMicrosoftSignIn() {
    this.isMicrosoftLoading = true;
    this.errorMessage = '';

    this.authService.getMicrosoftAuthorizeUrl().subscribe({
      next: (response) => {
        this.isMicrosoftLoading = false;
        window.location.href = response.url;
      },
      error: (error) => {
        this.isMicrosoftLoading = false;
        this.errorMessage = error?.error?.message || 'No se pudo iniciar sesión con Microsoft. Por favor intenta nuevamente';
      }
    });
  }

  onEmailChange(): void {
    if (this.errorMessage) {
      this.errorMessage = '';
    }
  }

  onPasswordChange(): void {
    if (this.errorMessage) {
      this.errorMessage = '';
    }
  }

  private mapOAuthError(errorCode: string): string {
    switch (errorCode) {
      case 'oauth_exchange_error':
        return 'No se pudo completar el inicio de sesión OAuth.';
      case 'oauth_login_error':
        return 'Ocurrió un error al iniciar sesión con el proveedor.';
      default:
        return 'No se pudo completar el inicio de sesión.';
    }
  }
}